import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CategoryCard from '@/components/base/CategoryCard';
import ArticleCard from '@/components/base/ArticleCard';
import FAQ from '@/components/base/FAQ';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles, getLatestArticles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';

export default function Home() {
  const latestArticles = getLatestArticles(6);
  const featuredArticles = articles.slice(0, 3);

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteInfo.url}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${siteInfo.name} - ${siteInfo.tagline}`,
    description: siteInfo.description,
    url: siteInfo.url,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <SEOHead
        title={`${siteInfo.name} - ${siteInfo.tagline}`}
        description={siteInfo.description}
        keywords={siteInfo.keywords}
        canonical={siteInfo.url}
        ogTitle={`${siteInfo.name} - ${siteInfo.tagline}`}
        ogDescription={siteInfo.description}
      />
      <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageJsonLd)}</script>

      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20digital%20streaming%20media%20landscape%20with%20flowing%20red%20crimson%20and%20deep%20amber%20light%20trails%20forming%20sweeping%20curves%20against%20a%20dark%20midnight%20background%20modern%20cinematic%20atmosphere%20with%20soft%20glowing%20particles%20and%20geometric%20accents%20clean%20minimal%20sophisticated%20design&width=1600&height=900&seq=hero-main-001&orientation=landscape"
            alt={`${siteInfo.name} 背景`}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
          <div className="relative z-10 w-full text-center px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h1 className="text-white text-3xl md:text-5xl font-bold mb-4 font-heading leading-tight">
                {siteInfo.name}
              </h1>
              <p className="text-white/80 text-base md:text-lg mb-3 max-w-xl mx-auto">
                {siteInfo.tagline}
              </p>
              <p className="text-white/60 text-sm md:text-base mb-8 max-w-2xl mx-auto leading-relaxed">
                {siteInfo.description}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/official-access"
                  className="whitespace-nowrap px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-md font-medium transition-all duration-200 text-sm cursor-pointer"
                >
                  <i className="ri-link mr-2"></i>查看官方入口
                </Link>
                <Link
                  to="/category/backup-access"
                  className="whitespace-nowrap px-8 py-3 bg-white/15 hover:bg-white/25 text-white border border-white/30 rounded-md font-medium transition-all duration-200 text-sm cursor-pointer backdrop-blur-sm"
                >
                  <i className="ri-shield-check-line mr-2"></i>备用入口
                </Link>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
                {siteInfo.keywords.split(',').slice(0, 4).map((kw) => (
                  <span key={kw} className="text-xs text-white/50 px-3 py-1 rounded-full border border-white/15 backdrop-blur-sm whitespace-nowrap">
                    {kw.trim()}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Featured Articles Section */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2 font-heading">
                精选推荐
              </h2>
              <p className="text-foreground-500 text-sm">为您精选最受欢迎的访问指南与教程</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.slug} {...article} />
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation Section */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2 font-heading">
                快速导航
              </h2>
              <p className="text-foreground-500 text-sm">按分类浏览，快速找到您需要的内容</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <CategoryCard key={cat.slug} {...cat} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles Section */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-1 font-heading">
                  最新文章
                </h2>
                <p className="text-foreground-500 text-sm">实时更新蘑菇TV最新资讯与使用指南</p>
              </div>
              <Link
                to="/category/latest-news"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} {...article} />
              ))}
            </div>
            <div className="mt-8 text-center sm:hidden">
              <Link
                to="/category/latest-news"
                className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部文章 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-primary-500">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {siteInfo.stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-white mb-1 font-heading">{stat.value}</div>
                  <div className="text-sm text-white/70">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <FAQ
              items={siteInfo.faqs.slice(0, 6)}
              title="常见问题解答"
              subtitle="关于蘑菇TV入口的常见疑问"
            />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 px-4 md:px-6 relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20flowing%20ribbons%20of%20deep%20red%20crimson%20and%20amber%20gold%20light%20against%20a%20dark%20background%20creating%20an%20elegant%20streaming%20media%20atmosphere%20with%20soft%20glowing%20particles%20cinematic%20minimal%20design&width=1600&height=600&seq=cta-footer-002&orientation=landscape"
            alt="CTA 背景"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/60"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 font-heading">
              找不到蘑菇TV入口？
            </h2>
            <p className="text-white/70 text-sm mb-8 leading-relaxed">
              收藏本页面，随时获取蘑菇TV最新官方入口地址。我们7×24小时监控入口状态，确保您第一时间访问成功。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/official-access"
                className="whitespace-nowrap px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-md font-medium transition-all duration-200 text-sm cursor-pointer"
              >
                <i className="ri-link mr-2"></i>立即访问官方入口
              </Link>
              <Link
                to="/about"
                className="whitespace-nowrap px-8 py-3 bg-white/15 hover:bg-white/25 text-white border border-white/30 rounded-md font-medium transition-all duration-200 text-sm cursor-pointer backdrop-blur-sm"
              >
                <i className="ri-information-line mr-2"></i>了解更多
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}